import type {
  Pagination,
  ProductData,
  ProductReview,
  ProductReviewsSummary,
  Survey,
} from '@shared/dream-components/src';

interface ProductVariantAsset {
  id: string;
  url: string;
  alt_text: string | null;
  position: number;
  is_default: boolean;
}

/** Webinar redeemable status (ProductRedeemables::Webinar) */
export const WEBINAR_REDEEMABLE_STATUS = {
  PENDING: 'pending',
  LIVE: 'live',
  ENDED: 'ended',
  CANCELLED: 'cancelled',
} as const;

export type WebinarRedeemableStatus = (typeof WEBINAR_REDEEMABLE_STATUS)[keyof typeof WEBINAR_REDEEMABLE_STATUS];

export interface ProductVariant {
  id: string;
  name: string | null;
  status: 'draft' | 'live' | 'archived';
  unit_amount: number;
  original_unit_amount?: number | null;
  currency: string;
  is_default: boolean;
  default_image_url?: string | null;
  stripe_price_id: string | null;
  created_at?: string;
  updated_at?: string;
  created_by_user_id?: string | null;
  updated_by_user_id?: string | null;
  redeemable?: {
    provider: 'cal_com' | 'daily_co' | null;
    type:
      | 'ProductRedeemables::CoachingSession'
      | 'ProductRedeemables::File'
      | 'ProductRedeemables::Link'
      | 'ProductRedeemables::Webinar';
    metadata: any;
    /** Webinar-only: returned by API for ProductRedeemables::Webinar */
    remaining_seats?: number;
    max_seats?: number;
    event_starts_at?: string;
    event_ends_at?: string;
    time_zone?: string;
    iana_time_zone?: string;
    status?: WebinarRedeemableStatus;
  } | null;
  price?: string | null;
  original_price?: string | null;
  assets?: ProductVariantAsset[];
  required_form_id?: string | null;
  required_form?: Survey | null;
}

export enum ProductType {
  FILE_DOWNLOAD = 'file',
  LINK_MEDIA = 'link',
  COACHING_SESSION = 'coaching_session',
  WEBINAR = 'webinar',
}

export interface Product {
  id: string;
  name: string;
  description: string | null;
  slug: string;
  status: 'draft' | 'live' | 'archived';
  cta_text: string | null;
  post_purchase_redirect_url?: string | null;
  product_type: ProductType | null;
  tiptap_state: ProductData['tiptap_state'];
  free: boolean;
  sold_out?: boolean;
  stripe_product_id?: string | null;
  created_at: string;
  updated_at: string;
  created_by_user_id?: string | null;
  updated_by_user_id?: string | null;
  default_product_variant?: ProductVariant | null;
  product_variants: ProductVariant[];
}
export interface ProductReviewsResponse {
  reviews: ProductReview[];
  pagination: Pagination;
  metrics?: ProductReviewsSummary;
}

export interface ProductReviewResponse {
  review: ProductReview;
  metrics?: ProductReviewsSummary;
}
